import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, useDisclosure, Tooltip } from '@heroui/react';
import { FC } from 'react';
import SearchModal from './SearchModal';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../core/rootHooks';
import { AppDispatch } from '../../core/store';
import { LuBuilding, LuList, LuListFilter, LuShip, LuWaves, LuBoxes } from 'react-icons/lu';
import { setSelectedVesselsForOutputRequest } from './searchSlice';
import { selectSelectionData, selectSubscribedVesselSearchFormData } from './searchSelectors';
import { ISelectedVesselsFormData, IVesselFormData } from '../../shared/sharedTypes';
import { EOutputType, ESelectionType } from '../../shared/sharedEnums';
import { selectVesselsLastSignalLoading } from '../main-map/slices/vesselsLastSignalSlice';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { setFeature, setSelection, setSubtab, setTab } from '../../routing/pageContextSlice';
import { clearSelectedPortGeometryRequest } from '../port/portSlice';
import { resetCohortSource } from '../common/cohort/cohortSourceSlice';

const Search: FC = () => {
	const dispatch = useAppDispatch<AppDispatch>();

	const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

	const { selectionType, selectionName } = useAppSelector(selectSelectionData);
	const vessels = useAppSelector(selectSubscribedVesselSearchFormData);

	const vesselsLastSignalLoading = useAppSelector(selectVesselsLastSignalLoading);

	const handleOnSearchModalClose = () => {
		onClose();
	};

	const handleSearchClear = () => {
		clearAndResetNavigation();
		dispatch(resetCohortSource());

		const outputSelection: ISelectedVesselsFormData = {
			selectedVesselsList: vessels.map((v) => ({
				name: v.name,
				imoNumber: v.imoNumber,
				sectorName: v.sector,
				companyName: v.companyName,
			})) as IVesselFormData[],
			outputType: vessels.length === 1 ? EOutputType.Vessel : vessels.length > 200 ? EOutputType.Summary : EOutputType.Fleet,
			selectionType: ESelectionType.None,
			selectionName: '',
		};
		dispatch(clearSelectedPortGeometryRequest());
		dispatch(setSelectedVesselsForOutputRequest({ outputSelection }));
	};

	const clearAndResetNavigation = () => {
		dispatch(setFeature(null));
		dispatch(setSelection({ type: ESelectionType.None }));
		dispatch(setTab(undefined));
		dispatch(setSubtab(undefined));
		// navigate('/', { replace: true });
		// window.history.replaceState({}, '', '/');
	};

	const renderSelectionIcon = () => {
		if (selectionType === 'Port') {
			return <LuBoxes />;
		} else if (selectionType === 'Vessel') {
			return <LuShip />;
		} else if (selectionType === 'Ownership') {
			return <LuBuilding />;
		} else if (selectionType === 'Sector') {
			return <LuWaves />;
		} else if (selectionType === ESelectionType.Portfolio || selectionType === ESelectionType.DraftPortfolio) {
			return <LuList />;
		} else if (selectionType === ESelectionType.Filter || selectionType === ESelectionType.DraftFilter) {
			return <LuListFilter />;
		} else {
			return <FontAwesomeIcon icon={faSearch} className="text-msi-darkgrey-color" />;
		}
	};

	const getSelectionName = () => {
		if (selectionType === ESelectionType.Portfolio) {
			return `${ESelectionType.Portfolio}: ${selectionName}`;
		} else if (selectionType === ESelectionType.Filter) {
			return `${ESelectionType.Filter}: ${selectionName}`;
		} else if (selectionType === ESelectionType.DraftPortfolio) {
			return `${ESelectionType.Portfolio}: ${selectionName} (Unsaved)`;
		} else if (selectionType === ESelectionType.DraftFilter) {
			return `${ESelectionType.Filter}: ${selectionName} (Unsaved)`;
		} else if (selectionType === ESelectionType.Ownership) {
			return `Owner: ${selectionName}`;
		} else if (selectionType === ESelectionType.Sector) {
			return `Sector: ${selectionName}`;
		} else if (selectionType === ESelectionType.Port) {
			return `Port: ${selectionName}`;
		} else {
			return selectionName;
		}
	};
	return (
		<>
			<div
				onClick={vesselsLastSignalLoading ? undefined : onOpen}
				className="flex items-center bg-msi-white-color text-msi-darkgrey-color p-1.5 hover:cursor-text border border-msi-darkgrey-color-300 rounded"
			>
				<div className="flex-none w-4">{renderSelectionIcon()}</div>
				<div className="grow flex ml-1 max-w-[calc(100%-20px)]">
					{selectionType !== ESelectionType.None ? (
						<div className="flex items-center justify-between w-full bg-default-100 rounded px-2 py-1 min-h-8">
							<Tooltip
								content={getSelectionName()}
								delay={0}
								closeDelay={0}
								placement="bottom-start"
								classNames={{
									content: 'bg-msi-white-color text-msi-darkgrey-color max-w-lg break-words whitespace-normal rounded',
									base: '-ml-2 transition-opacity duration-75',
								}}
							>
								<div className="flex-1 truncate text-sm text-foreground pr-2">{getSelectionName()}</div>
							</Tooltip>
							<div className="shrink-0">
								<FontAwesomeIcon
									className="cursor-pointer text-msi-darkgrey-color hover:text-danger"
									size="sm"
									onClick={(e) => {
										e.stopPropagation();
										handleSearchClear();
									}}
									icon={faClose}
								/>
							</div>
						</div>
					) : (
						<Chip variant="light" className="rounded min-w-full">
							Search (Port, Vessel, Ownership, Sector, Portfolio, Filter)
						</Chip>
					)}
				</div>
			</div>
			<SearchModal
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				handleOnSearchModalClose={handleOnSearchModalClose}
				clearAndResetNavigation={clearAndResetNavigation}
			/>
		</>
	);
};

export default Search;
